import type {
    BoardMove,
    BoardMovedItemGetter,
    BoardPlacement,
    BoardPlaceRule,
} from "./Board.types";
import {
    getCellKey,
    getPlacementKey,
    type BoardCellEntry,
    type BoardCells,
} from "./boardCells";
import { isSameDropTarget } from "./boardDropTargets";
import {
    getEntriesWithout,
    locateBoardEntry,
    resolveBoardDrop,
    type BoardDropResolution,
    type BoardDropTarget,
} from "./boardMove";
import type { BoardDragGroup, BoardMoveRule } from "./boardDragGroup";
import type { BoardRefusals } from "./boardMoveRules";
import {
    getHeldPlaceRefusal,
    isPlaceHeldInSort,
    isPlacingInSort,
} from "./boardPlaceSpot";

export type BoardDragState<TItem> =
    | {
          phase: "idle";
          focusKey: string | null;
          /** The drag that just ended was a keyboard drag. */
          isKeyboard: boolean;
      }
    | BoardDraggingState
    | ({
          phase: "settling";
          activeKey: string;
      } & BoardDropLanding<TItem>);

/** Where a drop lands: the preview kept until the overlay has landed on it. */
export interface BoardDropLanding<TItem> {
    cells: BoardCells<TItem>;
    focusKey: string;
    /** The other cards a group drop moved, in board order. */
    landingKeys: readonly string[];
}

export interface BoardDraggingState {
    phase: "dragging";
    activeKey: string;
    /** The selection carried along with the grabbed card, if any. */
    group: BoardDragGroup | null;
    target: BoardDropTarget;
    isKeyboard: boolean;
    /**
     * ⌘ (Ctrl off Mac) is held during a pointer drag, or the last arrow key
     * of a keyboard drag used it. See `isPlacingInSort`.
     */
    isPlaceHeld: boolean;
    /** The place modifier just changed: slots open and close without sliding. */
    isSlotInstant: boolean;
    /** Evaluated once at pick-up. */
    refusals: BoardRefusals;
    /** The lane the card was picked up in. */
    originLaneId: string | null;
}

/** The reason the hovered cell refuses the card, if it does. */
export const getTargetRefusal = ({ refusals, target }: BoardDraggingState) =>
    refusals.get(getCellKey(target.placement));

/** What drag state is resolved against: the latest props. */
export interface BoardDragModel<TItem> {
    cells: BoardCells<TItem>;
    /** Every cell in board order, which a group keeps. */
    cellOrder: readonly BoardPlacement[];
    compareItems?: (first: TItem, second: TItem) => number;
    getMovedItem?: BoardMovedItemGetter<TItem>;
    /** The sort accepts a position, so the place modifier places the card. */
    canPlaceBetween: boolean;
    /** Narrows `canPlaceBetween` to the spots it allows. */
    canPlaceAt?: BoardPlaceRule<TItem>;
    canMove?: BoardMoveRule<TItem>;
}

/** The order a drop follows: none (the target slot) when placing by hand. */
export const getDropOrder = <TItem>(
    model: BoardDragModel<TItem>,
    dragging: BoardDraggingState,
) => (isPlacingInSort(model, dragging) ? undefined : model.compareItems);

/** Over a refusing cell the card previews (and drops) back at its origin. */
export const resolveDragging = <TItem>(
    model: BoardDragModel<TItem>,
    dragging: BoardDraggingState,
): BoardDropResolution<TItem> => {
    if (getTargetRefusal(dragging) !== undefined) {
        return { cells: model.cells, move: null };
    }
    const resolution = resolveBoardDrop({
        cells: model.cells,
        activePlacementKey: dragging.activeKey,
        target: dragging.target,
        compareItems: getDropOrder(model, dragging),
        getMovedItem: model.getMovedItem,
    });
    return resolution.move !== null && isPlacingInSort(model, dragging)
        ? { ...resolution, move: { ...resolution.move, isPlacedInSort: true } }
        : resolution;
};

/**
 * The cells as the drag previews them. On a sorted board the slot sits
 * where the order puts the card, wherever the pointer is in the cell.
 */
export const getRenderedCells = <TItem>(
    model: BoardDragModel<TItem>,
    state: BoardDragState<TItem>,
) => {
    if (state.phase === "settling") {
        return state.cells;
    }
    return state.phase === "dragging"
        ? resolveDragging(model, state).cells
        : model.cells;
};

/**
 * The cell a sorted pointer drag would drop into, which shows the sort hint
 * in its slot (hidden while placing by hand). Not a refusing cell
 * (it shows its reason), and not the origin (dropping there changes
 * nothing) unless the place key is held where `canPlaceAt` refuses, so the
 * hint can say why.
 */
export const getSortHintCellKey = <TItem>(
    model: BoardDragModel<TItem>,
    state: BoardDragState<TItem>,
) => {
    if (
        state.phase !== "dragging" ||
        model.compareItems === undefined ||
        state.isKeyboard ||
        getTargetRefusal(state) !== undefined
    ) {
        return null;
    }
    const origin = locateBoardEntry(model.cells, state.activeKey)?.entry;
    const targetKey = getCellKey(state.target.placement);
    const isOrigin =
        origin === undefined || getCellKey(origin.placement) === targetKey;
    return isOrigin && getHeldPlaceRefusal(model, state) === undefined
        ? null
        : targetKey;
};

interface StartDragArgs<TItem> {
    cells: BoardCells<TItem>;
    activeKey: string;
    isKeyboard: boolean;
    getGroup: (grabbed: BoardCellEntry<TItem>) => BoardDragGroup | null;
    getRefusals: (
        grabbed: BoardCellEntry<TItem>,
        group: BoardDragGroup | null,
    ) => BoardRefusals;
}

export const startDrag = <TItem>({
    cells,
    activeKey,
    isKeyboard,
    getGroup,
    getRefusals,
}: StartDragArgs<TItem>): BoardDraggingState | null => {
    const location = locateBoardEntry(cells, activeKey);
    if (location === null) {
        return null;
    }
    const { placement } = location.entry;
    const group = getGroup(location.entry);
    return {
        phase: "dragging",
        activeKey,
        group,
        target: { placement, index: location.index },
        isKeyboard,
        isPlaceHeld: false,
        isSlotInstant: false,
        refusals: getRefusals(location.entry, group),
        originLaneId: placement.laneId,
    };
};

/**
 * A group whose cards keep their lanes drops in the grabbed card's lane of
 * the hovered column, at the end of that cell when another lane is hovered.
 */
const pinGroupLane = <TItem>(
    { cells }: BoardDragModel<TItem>,
    { group, originLaneId, activeKey }: BoardDraggingState,
    target: BoardDropTarget,
): BoardDropTarget => {
    if (!group?.isLaneFixed || target.placement.laneId === originLaneId) {
        return target;
    }
    const placement = { ...target.placement, laneId: originLaneId };
    return {
        placement,
        index: getEntriesWithout(cells, placement, activeKey).length,
    };
};

/**
 * Sorted cells place the card by the order and refusing cells don't take
 * it, so their index is 0: moving the pointer within one doesn't retarget,
 * and its slot stays put. A keyboard drag keeps its slot index over a
 * refusing cell instead, so stepping back out lands where it was. With the
 * place key held a sorted cell keeps the index even where `canPlaceAt`
 * refuses it, since that's the spot it asks about.
 */
const getTargetIndex = <TItem>(
    model: BoardDragModel<TItem>,
    current: BoardDraggingState,
    target: BoardDropTarget,
) => {
    if (
        model.compareItems !== undefined &&
        !isPlaceHeldInSort(model, current)
    ) {
        return 0;
    }
    if (current.refusals.has(getCellKey(target.placement))) {
        return current.isKeyboard ? current.target.index : 0;
    }
    return target.index;
};

/** The state for a new target, or null when nothing changes. */
export const retarget = <TItem>(
    model: BoardDragModel<TItem>,
    current: BoardDraggingState,
    target: BoardDropTarget | null,
): BoardDraggingState | null => {
    if (target === null) {
        return null;
    }
    const pinned = pinGroupLane(model, current, target);
    const normalized = {
        ...pinned,
        index: getTargetIndex(model, current, pinned),
    };
    return isSameDropTarget(normalized, current.target)
        ? null
        : { ...current, target: normalized };
};

export interface BoardDropOutcome<TItem> extends BoardDropLanding<TItem> {
    move: BoardMove<TItem> | null;
}

export const NO_LANDING_KEYS: readonly string[] = [];

export const getDropOutcome = <TItem>(
    model: BoardDragModel<TItem>,
    current: BoardDraggingState,
): BoardDropOutcome<TItem> => {
    const { cells, move } = resolveDragging(model, current);
    const location = locateBoardEntry(cells, current.activeKey);
    return {
        cells,
        move,
        landingKeys: NO_LANDING_KEYS,
        focusKey:
            move === null || location === null
                ? current.activeKey
                : getPlacementKey(location.entry.itemId, move.to),
    };
};

/** Keyboard drops end at once; pointer drops wait for the overlay to land. */
export const getStateAfterDrop = <TItem>(
    current: BoardDraggingState,
    { cells, focusKey, landingKeys }: BoardDropLanding<TItem>,
): BoardDragState<TItem> =>
    current.isKeyboard
        ? { phase: "idle", focusKey, isKeyboard: true }
        : {
              phase: "settling",
              activeKey: current.activeKey,
              cells,
              focusKey,
              landingKeys,
          };
